import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react'
import clsx from 'clsx'
import { ChatPageSidebar } from 'components/chat-page/ChatPageSidebar'
import { ModalOutlet } from 'providers/ModalProvider'
import { PortalToastContainer } from 'components/ui/PortalToastContainer'
import { CookieConsent } from 'components/cookie/CookieConsent'

type ShellContextValue = {
  toggleMobileSidebar: () => void
  toggleCollapsed: () => void
  collapsed: boolean
}

const ShellContext = createContext<ShellContextValue | null>(null)

export const useChatPageShell = () => {
  const context = useContext(ShellContext)
  if (!context) {
    throw new Error('useChatPageShell must be used within ChatPageShell')
  }
  return context
}

type ChatPageShellProps = PropsWithChildren

export const ChatPageShell = ({ children }: ChatPageShellProps) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)

  const contextValue = useMemo<ShellContextValue>(
    () => ({
      toggleMobileSidebar: () => setMobileOpen((value) => !value),
      toggleCollapsed: () => setCollapsed((value) => !value),
      collapsed,
    }),
    [collapsed],
  )

  return (
    <ShellContext.Provider value={contextValue}>
      <div
        className="tw-flex tw-h-[100dvh] tw-w-full tw-overflow-hidden tw-font-figtree"
        style={{ background: 'var(--page-bg)', color: 'var(--text-primary)' }}
      >
        <aside
          className={clsx(
            'tw-fixed lg:tw-static tw-inset-y-0 tw-left-0 tw-z-40 tw-w-[280px] tw-h-full tw-flex-shrink-0',
            'tw-transition-all tw-duration-300 tw-ease-in-out',
            mobileOpen ? 'tw-translate-x-0' : '-tw-translate-x-full lg:tw-translate-x-0',
            collapsed && 'lg:tw-ml-[-280px]',
          )}
          style={{ background: 'var(--page-sidebar-bg)', borderRight: '1px solid var(--page-border)' }}
        >
          <ChatPageSidebar onNavigate={() => setMobileOpen(false)} />
        </aside>

        {mobileOpen && (
          <div
            className="animate-fade-in-fast tw-fixed tw-inset-0 tw-z-30 tw-bg-black/50 lg:tw-hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        <main className="tw-relative tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
          {children}
          <PortalToastContainer topOffset={16} />
        </main>
      </div>

      <CookieConsent surface="page" />
      <ModalOutlet variant="dialog" />
    </ShellContext.Provider>
  )
}
